// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/verbaDeSellIn.hooks.ts

/** Vive FORA da linha, que segue sendo o do servidor: é daí que o desfazer tira o destino. */

import { useCallback, useState } from 'react';
import {
  SEM_EDICAO,
  aplicarVerbaIdeal,
  apagarONovoCusto,
  desfazerAVerba,
  digitarAVerba,
  marcarVerbaComoGravada,
  resolverCampoDaVerba,
  verbaDosPontosDeMargem,
} from './verbaDeSellIn';
import type {
  CampoDaVerba,
  DestinoDoDesfazer,
  EdicaoDaVerba,
  LinhaComVerba,
  OpcoesDoCampoDaVerba,
  OrigemDaVerba,
  UnidadeDaVerba,
  VerbaGravadaPeloMotor,
} from './verbaDeSellIn';

export type ContextoDaDigitacao = {
  precoDeConcorrencia?: number | null;
  novoCustoLiquido?: number | null;
};

/** **Origem vazia com verba vazia e a LIMPEZA**; "nao escreva" e a ausencia do objeto inteiro. */
export type VerbaParaGravar = {
  sellinAllowance: number | null;
  sellinAllowanceOrigin: OrigemDaVerba | null;
};

/** Nada aqui é "não escreva a verba"; `sellinAllowance` nulo é "apague a verba". */
const verbaParaGravarDaEdicao = (edicao: EdicaoDaVerba): VerbaParaGravar | null => {
  if (edicao.origem === null) return null;

  // Com valor recusado, gravar o ultimo ACEITO persistiria um numero que nao esta na tela.
  if (edicao.recusa !== null) return null;

  return { sellinAllowance: edicao.valor, sellinAllowanceOrigin: edicao.origem };
};

export const useVerbaDeSellIn = () => {
  const [edicoes, setEdicoes] = useState<Record<string, EdicaoDaVerba>>({});
  const [unidades, setUnidades] = useState<Record<string, UnidadeDaVerba>>({});

  const edicaoDaLinha = useCallback((identidade: string): EdicaoDaVerba => edicoes[identidade] ?? SEM_EDICAO, [edicoes]);

  const unidadeDaLinha = useCallback((identidade: string): UnidadeDaVerba => unidades[identidade] ?? 'REAIS', [unidades]);

  const gravarEdicao = useCallback((identidade: string, proxima: EdicaoDaVerba) => {
    setEdicoes(anterior => ({ ...anterior, [identidade]: proxima }));
  }, []);

  /** Chega NA UNIDADE da linha; o canônico é sempre em reais no custo líquido. */
  const digitar = useCallback(
    (identidade: string, valorNaUnidade: number | null, contexto: ContextoDaDigitacao) => {
      const emReais =
        unidadeDaLinha(identidade) === 'REAIS'
          ? valorNaUnidade
          : verbaDosPontosDeMargem(valorNaUnidade, contexto.precoDeConcorrencia);

      gravarEdicao(identidade, digitarAVerba(edicaoDaLinha(identidade), emReais, contexto.novoCustoLiquido));
    },
    [edicaoDaLinha, gravarEdicao, unidadeDaLinha],
  );

  /**
   * Desfazer e gravar sao o mesmo clique, sem render no meio, e ele TEM de gravar: so limpar o
   * estado local devolve o campo a verba da LINHA, que ainda e a gravada.
   */
  const desfazer = useCallback(
    (identidade: string, destino: DestinoDoDesfazer): VerbaParaGravar => {
      gravarEdicao(identidade, desfazerAVerba(edicaoDaLinha(identidade)));

      return { sellinAllowance: destino.verba, sellinAllowanceOrigin: destino.origem };
    },
    [edicaoDaLinha, gravarEdicao],
  );

  /** Sem sugestão a linha não é tocada: gravaria verba VAZIA com origem `sugerido`. */
  const aplicarIdeal = useCallback(
    (identidade: string, verbaSugerida: number | null | undefined): VerbaParaGravar | null => {
      const proxima = aplicarVerbaIdeal(edicaoDaLinha(identidade), verbaSugerida);

      if (proxima.valor === null) return null;

      gravarEdicao(identidade, proxima);

      return verbaParaGravarDaEdicao(proxima);
    },
    [edicaoDaLinha, gravarEdicao],
  );

  const descartarPorFaltaDeNovoCusto = useCallback(
    (identidade: string) => gravarEdicao(identidade, apagarONovoCusto(edicaoDaLinha(identidade))),
    [edicaoDaLinha, gravarEdicao],
  );

  const alternarUnidade = useCallback((identidade: string) => {
    setUnidades(anterior => ({ ...anterior, [identidade]: (anterior[identidade] ?? 'REAIS') === 'REAIS' ? 'PONTOS' : 'REAIS' }));
  }, []);

  const limparEdicoes = useCallback(() => setEdicoes({}), []);

  /**
   * {@link marcarComoGravada} não alcança a massa: aquela desiste sem edição local, e na massa
   * nenhuma linha tem. Quem escreve cada edição continua sendo `marcarVerbaComoGravada`.
   */
  const adotarEdicoes = useCallback((aplicadas: Record<string, EdicaoDaVerba>) => {
    if (Object.keys(aplicadas).length === 0) return;

    setEdicoes(anterior => ({ ...anterior, ...aplicadas }));
  }, []);

  /**
   * **Lê a edição do próprio estado, e não da captura do render**, que traria o de antes do
   * clique. **O segundo argumento é obrigatório** porque em verba sugerida o motor a recalcula.
   */
  const marcarComoGravada = useCallback((identidade: string, gravado: VerbaGravadaPeloMotor) => {
    setEdicoes(anterior => {
      const edicao = anterior[identidade] ?? SEM_EDICAO;
      if (edicao.origem === null) return anterior;

      return { ...anterior, [identidade]: marcarVerbaComoGravada(edicao, gravado) };
    });
  }, []);

  const campoDaLinha = useCallback(
    (identidade: string, linha: LinhaComVerba, opcoes: OpcoesDoCampoDaVerba = {}): CampoDaVerba =>
      resolverCampoDaVerba(linha, edicaoDaLinha(identidade), opcoes),
    [edicaoDaLinha],
  );

  /**
   * A verba ainda NÃO gravada, e a única pergunta que a tela faz em QUALQUER gravação da linha.
   *
   * **A verba já gravada nunca é reenviada:** a escrita no motor é parcial, chave ausente
   * preserva as duas colunas, e reenviar a do líquido sem o valor ao fornecedor apaga a âncora.
   */
  const verbaPendenteDeGravacao = useCallback(
    (identidade: string): VerbaParaGravar | null => {
      const edicao = edicaoDaLinha(identidade);

      if (edicao.sincronizada) return null;

      return verbaParaGravarDaEdicao(edicao);
    },
    [edicaoDaLinha],
  );

  return {
    campoDaLinha,
    edicaoDaLinha,
    unidadeDaLinha,
    digitar,
    desfazer,
    aplicarIdeal,
    descartarPorFaltaDeNovoCusto,
    alternarUnidade,
    limparEdicoes,
    adotarEdicoes,
    marcarComoGravada,
    verbaPendenteDeGravacao,
  };
};

export type UseVerbaDeSellIn = ReturnType<typeof useVerbaDeSellIn>;
